import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Plus, Pencil, Trash2 } from 'lucide-react';

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', sort_order: 0, active: true, type: 'standard' });

  const load = useCallback(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.name) { toast.error('Name ist Pflichtfeld'); return; }
    try {
      if (editing) {
        await api.put(`/categories/${editing}`, form);
        toast.success('Kategorie aktualisiert');
      } else {
        await api.post('/categories', form);
        toast.success('Kategorie erstellt');
      }
      setForm({ name: '', sort_order: 0, active: true, type: 'standard' });
      setEditing(null);
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const handleEdit = (c) => {
    setForm({ name: c.name, sort_order: c.sort_order || 0, active: c.active !== false, type: c.type || 'standard' });
    setEditing(c.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    try { await api.delete(`/categories/${id}`); toast.success('Gelöscht'); load(); }
    catch { toast.error('Fehler'); }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0E518D]">Kategorien</h1>
        <Button onClick={() => { setForm({ name: '', sort_order: 0, active: true, type: 'standard' }); setEditing(null); setDialogOpen(true); }}
          className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide text-xs" data-testid="add-category-btn">
          <Plus className="h-4 w-4 mr-1" /> Neue Kategorie
        </Button>
      </div>

      <div className="bg-white rounded-sm shadow-sm border border-slate-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-wide">Name</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Typ</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Sortierung</TableHead>
              <TableHead className="text-xs uppercase tracking-wide">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-right">Aktionen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Keine Kategorien vorhanden</TableCell></TableRow>
            ) : categories.map(c => (
              <TableRow key={c.id} data-testid={`category-row-${c.id}`}>
                <TableCell className="text-sm font-medium">{c.name}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{c.type === 'package' ? 'Paket' : 'Standard'}</Badge></TableCell>
                <TableCell className="text-sm">{c.sort_order}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={c.active ? 'border-green-500 text-green-600 text-xs' : 'border-slate-300 text-slate-500 text-xs'}>
                    {c.active ? 'Aktiv' : 'Inaktiv'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(c)} data-testid={`edit-category-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="text-red-500" data-testid={`delete-category-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0E518D]">{editing ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm">Name *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-sm" data-testid="category-name" />
            </div>
            <div>
              <Label className="text-sm">Typ</Label>
              <select
                value={form.type}
                onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                className="mt-1 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E518D] focus:border-transparent"
                data-testid="category-type"
              >
                <option value="standard">Standard</option>
                <option value="package">Urlaubspaket</option>
              </select>
            </div>
            <div>
              <Label className="text-sm">Sortierung</Label>
              <Input type="number" value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} className="mt-1 rounded-sm" data-testid="category-sort-order" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={v => setForm(f => ({ ...f, active: v }))} data-testid="category-active" />
              <Label className="text-sm">Aktiv</Label>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm uppercase tracking-wide text-xs">Abbrechen</Button>
            <Button onClick={handleSave} className="bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide text-xs" data-testid="category-save">{editing ? 'Aktualisieren' : 'Erstellen'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
